import { useCallback, useRef, useState, useMemo } from 'react';

/**
 * A generic hook to manage a history stack for undo/redo functionality.
 * Optimized for snapshot-based state management (e.g. FileSystem state).
 */
export function useHistory<T>(maxHistory: number = 50) {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  const historyRef = useRef<T[]>([]);
  const futureRef = useRef<T[]>([]);

  const pushState = useCallback((state: T) => {
    // Clone state to prevent mutations in history
    const clonedSnapshot = structuredClone(state);
    
    historyRef.current.push(clonedSnapshot);
    if (historyRef.current.length > maxHistory) {
      historyRef.current.shift();
    }
    
    // Clear future when a new action is performed
    futureRef.current = [];
    
    setCanUndo(true);
    setCanRedo(false);
  }, [maxHistory]);

  const undo = useCallback((currentState: T): T | null => {
    if (historyRef.current.length === 0) return null;
    
    const previousState = historyRef.current.pop()!;
    futureRef.current.push(structuredClone(currentState));
    
    setCanUndo(historyRef.current.length > 0);
    setCanRedo(true);
    
    return previousState;
  }, []);

  const redo = useCallback((currentState: T): T | null => {
    if (futureRef.current.length === 0) return null;
    
    const nextState = futureRef.current.pop()!;
    historyRef.current.push(structuredClone(currentState));
    
    setCanUndo(true);
    setCanRedo(futureRef.current.length > 0);
    
    return nextState;
  }, []);

  const clearHistory = useCallback(() => {
    historyRef.current = [];
    futureRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
  }, []);

  return useMemo(() => ({
    pushState,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory
  }), [pushState, undo, redo, canUndo, canRedo, clearHistory]);
}
